import { corporateEventDates } from './corporateEvents';
/**
 * Placement edges decide where a company sits (its owner). Higher wins, matching
 * pangaea's repository.PlacementRank: acquisition / merger > subsidiary >
 * jv_member (the JV sits under its members).
 */ const PLACEMENT_RANK = {
    acquisition: 3,
    merger: 3,
    subsidiary: 2,
    jv_member: 1
};
/** History links: shown as labels on the company, never decide its parent. */ const HISTORY = new Set([
    'rename',
    'spinoff',
    'novation'
]);
function childParent(e) {
    return e.relationshipType === 'jv_member' ? [
        e.toCompanyId,
        e.fromCompanyId
    ] : [
        e.fromCompanyId,
        e.toCompanyId
    ];
}
const monthYear = (e)=>e.effectiveStart ? ` ${e.effectiveStart.toDate().toLocaleDateString('en-GB', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC'
    })}` : '';
const isOpen = (e, now)=>!e.effectiveEnd || e.effectiveEnd.toDate().getTime() > now;
/** How a child relates to the parent it sits under, as the card's eyebrow. */ function placementLabel(e, jvMembers) {
    switch(e.relationshipType){
        case 'subsidiary':
            return 'Subsidiary';
        case 'acquisition':
            return `Acquired${monthYear(e)}`;
        case 'merger':
            return `Merged in${monthYear(e)}`;
        case 'jv_member':
            return jvMembers.length > 1 ? `JV · with ${jvMembers.join(', ')}` : 'Joint venture';
        default:
            return e.relationshipType.replace(/_/g, ' ');
    }
}
/** A placement edge that was not chosen (e.g. an ended owner), as a history label. */ function formerOwnerLabel(e, other) {
    const until = e.effectiveEnd ? ` until ${e.effectiveEnd.toDate().toLocaleDateString('en-GB', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC'
    })}` : '';
    switch(e.relationshipType){
        case 'subsidiary':
            return `Subsidiary of ${other}${until}`;
        case 'acquisition':
            return `Acquired by ${other}${monthYear(e)}`;
        case 'merger':
            return `Merged into ${other}${monthYear(e)}`;
        default:
            return '';
    }
}
/**
 * Turn GetCompanyFamily into an ownership tree plus side lists.
 *
 * Each company sits under one owner, chosen from its placement edges: an open
 * edge over an ended one, then acquisition / merger over subsidiary over JV
 * membership, then the parent closest to the root, then the most recent start.
 * Rename, spin-off and novation never move a company: they become labels on its
 * card ("Spun off from BMC Sep 2025", "contracts novated"), as do owners that
 * were not chosen. Two open ownership parents is a data conflict and is flagged.
 */ export function buildFamilyTree(resp, now = Date.now()) {
    if (!resp) return {
        nodes: [],
        channel: [],
        other: [],
        rootName: ''
    };
    const name = new Map(resp.nodes.map((n)=>[
            n.companyId,
            n.name
        ]));
    const nameOf = (id)=>name.get(id) ?? 'a company';
    const placementOf = new Map();
    const placementChildren = new Map();
    const historyOf = new Map();
    const inTree = new Set([
        resp.rootCompanyId,
        resp.focusCompanyId
    ]);
    const channel = [];
    const other = [];
    for (const e of resp.edges){
        const t = e.relationshipType;
        if (PLACEMENT_RANK[t]) {
            const [c, p] = childParent(e);
            placementOf.set(c, [
                ...placementOf.get(c) ?? [],
                e
            ]);
            placementChildren.set(p, [
                ...placementChildren.get(p) ?? [],
                c
            ]);
            inTree.add(c);
            inTree.add(p);
            continue;
        }
        if (HISTORY.has(t)) {
            for (const id of [
                e.fromCompanyId,
                e.toCompanyId
            ]){
                historyOf.set(id, [
                    ...historyOf.get(id) ?? [],
                    e
                ]);
                inTree.add(id);
            }
            continue;
        }
        const row = {
            id: e.relationshipId || `${e.fromCompanyId}>${e.toCompanyId}`,
            fromId: e.fromCompanyId,
            fromName: name.get(e.fromCompanyId) ?? 'A company',
            toId: e.toCompanyId,
            toName: nameOf(e.toCompanyId),
            sentence: '',
            dates: corporateEventDates(e.effectiveStart, e.effectiveEnd)
        };
        if (t === 'reseller') {
            row.sentence = `${row.fromName} sells through ${row.toName}`;
            channel.push(row);
        } else {
            row.sentence = t === 'affiliate' ? `${row.fromName} and ${row.toName} are affiliates` : `${row.fromName} and ${row.toName}: joint venture`;
            other.push(row);
        }
    }
    // Distance from the root through any placement edge, for the JV tie-break.
    const rootDepth = new Map([
        [
            resp.rootCompanyId,
            0
        ]
    ]);
    const queue = [
        resp.rootCompanyId
    ];
    while(queue.length){
        const cur = queue.shift();
        for (const c of placementChildren.get(cur) ?? []){
            if (!rootDepth.has(c)) {
                rootDepth.set(c, (rootDepth.get(cur) ?? 0) + 1);
                queue.push(c);
            }
        }
    }
    // Pick one owner per company.
    const chosen = new Map();
    const conflict = new Set();
    for (const id of inTree){
        if (id === resp.rootCompanyId) continue;
        const ps = [
            ...placementOf.get(id) ?? []
        ].sort((a, b)=>{
            const oa = isOpen(a, now) ? 0 : 1;
            const ob = isOpen(b, now) ? 0 : 1;
            if (oa !== ob) return oa - ob;
            const ra = PLACEMENT_RANK[a.relationshipType] ?? 0;
            const rb = PLACEMENT_RANK[b.relationshipType] ?? 0;
            if (ra !== rb) return rb - ra;
            const da = rootDepth.get(childParent(a)[1]) ?? 99;
            const db = rootDepth.get(childParent(b)[1]) ?? 99;
            if (da !== db) return da - db;
            return (b.effectiveStart?.toDate().getTime() ?? 0) - (a.effectiveStart?.toDate().getTime() ?? 0);
        });
        if (ps[0]) chosen.set(id, ps[0]);
        const openOwners = new Set(ps.filter((e)=>isOpen(e, now) && (PLACEMENT_RANK[e.relationshipType] ?? 0) >= 2).map((e)=>childParent(e)[1]));
        if (openOwners.size > 1) conflict.add(id);
    }
    // Depth along the chosen owners, so parents are listed before children.
    const depthOf = (id)=>{
        let d = 0;
        let cur = id;
        const seen = new Set([
            cur
        ]);
        while(chosen.has(cur) && d < 50){
            cur = childParent(chosen.get(cur))[1];
            if (seen.has(cur)) break;
            seen.add(cur);
            d++;
        }
        return d;
    };
    const nodes = [];
    for (const id of [
        ...inTree
    ].sort((a, b)=>depthOf(a) - depthOf(b))){
        const via = chosen.get(id);
        const parentId = via ? childParent(via)[1] : null;
        const jvMembers = (placementOf.get(id) ?? []).filter((e)=>e.relationshipType === 'jv_member').map((e)=>nameOf(e.fromCompanyId));
        const parts = [
            via ? placementLabel(via, jvMembers) : id === resp.rootCompanyId ? 'Ultimate parent' : 'Company'
        ];
        // Owners that were not chosen (an ended parent, a second acquirer).
        for (const e of placementOf.get(id) ?? []){
            if (e === via || e.relationshipType === 'jv_member') continue;
            const label = formerOwnerLabel(e, nameOf(childParent(e)[1]));
            if (label) parts.push(label);
        }
        for (const e of historyOf.get(id) ?? []){
            const outgoing = e.fromCompanyId === id;
            if (e.relationshipType === 'novation' && outgoing) {
                parts.push(e.toCompanyId === parentId ? 'contracts novated' : `contracts novated to ${nameOf(e.toCompanyId)}`);
            } else if (e.relationshipType === 'spinoff' && outgoing) {
                parts.push(`Spun off from ${nameOf(e.toCompanyId)}${monthYear(e)}`);
            } else if (e.relationshipType === 'rename') {
                parts.push(outgoing ? `Renamed to ${nameOf(e.toCompanyId)}${monthYear(e)}` : `Formerly ${nameOf(e.fromCompanyId)}`);
            }
        }
        nodes.push({
            id,
            parentId,
            seat: {
                seatTitle: parts.join(' · '),
                holder: name.get(id) ?? 'Company',
                since: via?.effectiveEnd ? corporateEventDates(undefined, via.effectiveEnd) : undefined
            },
            ...conflict.has(id) ? {
                departure: {
                    label: 'Data conflict: more than one current owner'
                }
            } : {},
            selectable: true
        });
    }
    return {
        nodes,
        channel,
        other,
        rootName: name.get(resp.rootCompanyId) ?? ''
    };
}
